/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Trash2,
  Check
} from 'lucide-react';
import api from '../services/api';
import ViolationModal from '../components/ViolationModal';

const ResumeUpload = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Profile history state
  const [profileHistory, setProfileHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [violationData, setViolationData] = useState(null);

  // Load profile history on mount
  const loadProfileHistory = useCallback(async () => {
    if (!user) return;

    setLoadingHistory(true);
    try {
      const userId = user.id || user._id;
      const response = await api.getProfileHistory(userId);
      if (response.success) {
        setProfileHistory(response.data.profiles || []);
      }
    } catch (err) {
      console.error('Failed to load profile history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfileHistory();
  }, [loadProfileHistory]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];

    if (!validTypes.includes(selectedFile.type)) {
      setError('Please upload a PDF or DOCX file');
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResult(null);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file || !user) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('resume', file);

      const userId = user.id || user._id;
      console.log('Uploading with userId:', userId);

      const response = await api.uploadResume(formData, userId);

      if (response.success) {
        setResult(response.data);
        setFile(null);
        // Reload profile history after upload
        await loadProfileHistory();
      } else if (response.blocked || response.error === 'CONTENT_POLICY_VIOLATION' || response.error === 'ACCOUNT_SUSPENDED') {
        // Guard Rails violation detected — show detailed modal
        setViolationData(response);
      } else {
        setError(response.error || 'Upload failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to upload profile');
    } finally {
      setUploading(false);
    }
  };

  const handleActivateProfile = async (resumeId) => {
    setActivatingId(resumeId);
    try {
      const response = await api.activateProfile(resumeId);
      if (response.success) {
        await loadProfileHistory();
      } else {
        setError(response.error || 'Failed to activate profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to activate profile');
    } finally {
      setActivatingId(null);
    }
  };

  const handleDeleteProfile = async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this profile? This action cannot be undone.')) {
      return;
    }

    setDeletingId(resumeId);
    try {
      const response = await api.deleteProfile(resumeId);
      if (response.success) {
        await loadProfileHistory();
      } else {
        setError(response.error || 'Failed to delete profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete profile');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const activeProfile = profileHistory.find(p => p.isActive);
  const previousProfiles = profileHistory.filter(p => !p.isActive);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Upload Your Profile</h1>
        <p className="text-gray-600">
          Upload your resume or CV to generate a personalized learning roadmap
        </p>
      </div>

      {/* Current Active Profile */}
      {activeProfile && (
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-cyan-500">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-cyan-500" />
              Current Active Profile
            </h2>
            <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium">
              Active
            </span>
          </div>
          <div className="flex items-center gap-4">
            <FileText className="w-10 h-10 text-cyan-500" />
            <div className="flex-1">
              <p className="font-medium text-gray-800">{activeProfile.fileName}</p>
              <div className="flex gap-4 text-sm text-gray-500">
                <span>{formatFileSize(activeProfile.fileSize)}</span>
                <span>{activeProfile.skillsCount} skills detected</span>
                <span>Uploaded {formatDate(activeProfile.uploadedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            dragActive
              ? 'border-cyan-500 bg-cyan-50'
              : 'border-gray-300 hover:border-cyan-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="space-y-4">
              <FileText className="w-16 h-16 mx-auto text-cyan-500" />
              <div>
                <p className="font-medium text-gray-800">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
              </div>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload Profile'}
                </button>
                <button
                  onClick={() => setFile(null)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Upload className="w-16 h-16 mx-auto text-gray-400" />
              <div>
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Drag and drop your profile document here
                </p>
                <p className="text-sm text-gray-500 mb-4">or</p>
                <label className="inline-block px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors cursor-pointer">
                  Browse Files
                  <input
                    name="resume"
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileInput}
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Supports PDF, DOC, DOCX (Max 5MB)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Error</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Upload Success */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800 text-lg">
                Profile Uploaded Successfully!
              </p>
              <p className="text-sm text-green-600">
                We have extracted information from your profile
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-700">File Name</p>
              <p className="text-gray-600">{result.fileName}</p>
            </div>

            {result.parsedSkills > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Skills Detected: {result.parsedSkills}
                </p>
              </div>
            )}
          </div>

          <div className="mt-4 flex gap-3">
            <button
              onClick={() => setResult(null)}
              className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
            >
              Upload Another
            </button>
          </div>
        </div>
      )}

      {/* Profile History Section */}
      {previousProfiles.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-700">
                Previous Profiles ({previousProfiles.length})
              </span>
            </div>
            {showHistory ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showHistory && (
            <div className="divide-y divide-gray-100">
              {previousProfiles.map((profile) => (
                <div
                  key={profile._id}
                  className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <FileText className="w-8 h-8 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-700">{profile.fileName}</p>
                      <div className="flex gap-3 text-sm text-gray-500">
                        <span>{formatFileSize(profile.fileSize)}</span>
                        <span>{profile.skillsCount} skills</span>
                        <span>{formatDate(profile.uploadedAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleActivateProfile(profile._id)}
                      disabled={activatingId === profile._id}
                      className="px-3 py-1.5 bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200 transition-colors disabled:opacity-50 flex items-center gap-1 text-sm font-medium"
                    >
                      <Check className="w-4 h-4" />
                      {activatingId === profile._id ? 'Activating...' : 'Make Active'}
                    </button>
                    <button
                      onClick={() => handleDeleteProfile(profile._id)}
                      disabled={deletingId === profile._id}
                      className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 flex items-center gap-1 text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                      {deletingId === profile._id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Loading state for history */}
      {loadingHistory && (
        <div className="text-center py-4 text-gray-500">
          Loading profile history...
        </div>
      )}

      {/* Violation Modal */}
      <ViolationModal
        show={!!violationData}
        onClose={() => setViolationData(null)}
        violationData={violationData}
      />
    </div>
  );
};

export default ResumeUpload;
